import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@design-system';
import { ChevronDown } from 'lucide-react';
import { manhattanLifeOmniFlexStcStates } from '@shared';
import { consumerQuoteKey } from '@shared/consumerPlan';
import { CoverageLaneSwitch } from '../components/coverage-lanes';
import { FinishPlanAction } from '../components/finish-plan-action';
import { NoQuotesNotice } from '../components/no-quotes-notice';
import { OtherWaysToPlan } from '../components/other-ways-to-plan';
import { PolicyReviewInProgressNotice } from '../components/policy-review-in-progress-notice';
import { QuoteOptionCard } from '../components/quote-option-card';
import { ReplaceSelectedPolicyDialog } from '../components/replace-selected-policy-dialog';
import { ScenarioSummary } from '../components/scenario-summary';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useCenterScrollChild } from '../hooks/use-center-scroll-child';
import { useCoverageSelection } from '../hooks/use-coverage-selection';
import { useSelectQuote } from '../hooks/use-select-quote';
import { coverageLanes } from '../lib/coverage-lanes';
import { stateName } from '../lib/format';
import { quoteDisplayName } from '../lib/policy-name';
import { searchOrigin } from '../lib/search-origin';
import type { ConsumerStepProps } from './step-props';

export function OptionsStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const [areDetailsOpen, setAreDetailsOpen] = useState(false);
  const {
    schedule,
    options,
    optionsBySchedule,
    projections,
    plan,
    updatePlan,
    scenario,
    usaState,
    noQuotesReason,
  } = useCoverageSelection(clientId);
  const selection = useSelectQuote(clientId, () => goTo('coverage'));
  if (!scenario) return null;

  const lanes = coverageLanes(optionsBySchedule);
  const switchLane = (paymentSchedule: ConsumerPaymentSchedule) => updatePlan({ paymentSchedule });
  const middle = options.find(option => option.label === 'bestValue') ?? options[Math.floor((options.length - 1) / 2)];
  const { containerRef, childRef } = useCenterScrollChild(middle && consumerQuoteKey(middle.quote));

  const isShortTermCareAvailable = (manhattanLifeOmniFlexStcStates as readonly string[]).includes(usaState ?? '');
  // The consumer's earlier choice stays highlighted; otherwise the middle option is.
  const recommended = options.find(option => option.label === 'bestValue') ?? options[0];
  const highlightedKey = options.some(option => consumerQuoteKey(option.quote) === plan?.selectedQuoteKey)
    ? plan?.selectedQuoteKey
    : recommended && consumerQuoteKey(recommended.quote);
  const replacementOption = options.find(
    option => consumerQuoteKey(option.quote) === selection.pendingReplacement?.quoteKey,
  );

  return (
    <StepFrame
      step="options"
      title="Which option feels like the right fit?"
      subtitle={<ScenarioSummary scenario={scenario} variant="sentence" />}
      onBack={() => back(searchOrigin(clientId))}
      help={
        <>
          <p>Choose the premium you’re comfortable considering. We’ve highlighted the middle option as a balanced starting point. The next page shows how your choice covers your care.</p>
          <p>
            <strong>Lower monthly cost</strong> options have smaller ongoing premiums that may increase.{' '}
            <strong>Balanced</strong> options finish paying after 10 years. <strong>Maximum protection</strong>{' '}
            options take one upfront payment. Coverage starts when the policy takes effect either way.
          </p>
          <p>The monthly max and lifetime benefit are this policy’s limits in the year care starts, not a promise that you’ll use all of them.</p>
          <div className="flex flex-col items-start">
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('compare', { returnTo: 'options' })}>
              Compare policy terms
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('premium-range', { returnTo: 'options' })}>
              Request a different premium range
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('household', { returnTo: 'options' })}>
              {plan?.partner ? 'Review partner information' : 'Add a partner'}
            </Button>
          </div>
        </>
      }
      next="see how this option covers your care."
    >
      {plan?.partner && (
        <p className="text-muted-foreground text-sm">
          Showing your options. {plan.partner.firstName}’s options will be added once their intake is reviewed.
        </p>
      )}

      <PolicyReviewInProgressNotice clientId={clientId} />

      {selection.chooseFailed && (
        <Alert variant="destructive">
          <AlertDescription>We couldn’t add this option to your plan. Please try again.</AlertDescription>
        </Alert>
      )}

      {options.length > 0 && <CoverageLaneSwitch lanes={lanes} value={schedule} onChange={switchLane} />}

      {options.length === 0 ? (
        <NoQuotesNotice clientId={clientId} returnTo="options" reason={noQuotesReason} usaState={usaState} />
      ) : (
        <div
          ref={containerRef}
          className="flex w-full min-w-0 snap-x snap-mandatory gap-4 overflow-x-auto max-md:py-1 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden md:grid md:snap-none md:grid-cols-3 md:overflow-visible"
        >
          {options.map(option => {
            const key = consumerQuoteKey(option.quote);
            return (
              <div
                key={key}
                ref={option === middle ? childRef : undefined}
                className="w-[85%] shrink-0 snap-center md:w-auto md:shrink"
              >
                <QuoteOptionCard
                  option={option}
                  projection={projections.get(key)}
                  careStartAge={scenario.careStartAge}
                  yearsUntilCare={scenario.yearsUntilCare}
                  isHighlighted={key === highlightedKey}
                  isRecommended={option.label === 'bestValue'}
                  areDetailsOpen={areDetailsOpen}
                  onDetailsOpenChange={setAreDetailsOpen}
                  className="h-full"
                  onChoose={() => selection.choose(key)}
                  isChoosing={selection.choosingQuoteKey === key}
                  isChoiceDisabled={!!selection.choosingQuoteKey}
                />
              </div>
            );
          })}
        </div>
      )}

      {options.length > 0 && <OtherWaysToPlan clientId={clientId} returnTo="options" appearance="links" />}

      <FinishPlanAction clientId={clientId} />

      {schedule === 'ongoing' && !isShortTermCareAvailable && (
        <Alert variant="default" data-testid="consumer-short-term-unavailable">
          <AlertTitle>Short-term care</AlertTitle>
          <AlertDescription>Quotes aren’t available in {stateName(usaState)} yet.</AlertDescription>
        </Alert>
      )}

      <Collapsible className="group/why">
        <CollapsibleTrigger asChild>
          <Button variant="link" className="px-0 has-[>svg]:px-0">
            Why could a policy pay less than its maximum?
            <ChevronDown className="transition-transform duration-200 ease-out group-data-[state=open]/why:rotate-180" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="text-muted-foreground flex flex-col gap-2 text-sm">
          <p>
            We project your care month by month. In some months your care bill is lower than the policy’s
            monthly limit, and benefits stop when care ends, so you may not use the full amount.
          </p>
          <p>Payments also start after a waiting period, under the policy’s terms.</p>
        </CollapsibleContent>
      </Collapsible>

      <p className="text-muted-foreground text-xs">
        Estimates for this care scenario. Insurance premiums are separate from care costs.
      </p>

      <ReplaceSelectedPolicyDialog
        replacement={selection.pendingReplacement}
        newPolicyName={replacementOption ? quoteDisplayName(replacementOption.quote) : 'the new option'}
        onConfirm={selection.confirmReplacement}
        onCancel={selection.cancelReplacement}
      />
    </StepFrame>
  );
}
